import { useState } from 'react';

import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Menu,
  Modal,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDots } from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import {
  MONITORING_ENTITLEMENT_STATE_LABELS,
  MonitoringEntitlement,
  MonitoringEntitlementAction,
  MonitoringEntitlementChange,
} from '@/types/monitoring';

const STATE_COLOURS: Record<MonitoringEntitlement['state'], string> = {
  active: 'green',
  suspended: 'orange',
  ended: 'gray',
};

interface MonitoringEntitlementControlsProps {
  teamId: string;
  teamName: string;
  /** Undefined when the team has never had monitoring. */
  entitlement: MonitoringEntitlement | undefined;
  onSaveDetails: (
    teamId: string,
    details: { contractRef: string | null; notes: string | null }
  ) => Promise<unknown>;
  onChange: (
    teamId: string,
    action: MonitoringEntitlementAction
  ) => Promise<MonitoringEntitlementChange>;
}

interface Confirmation {
  action: MonitoringEntitlementAction;
  title: string;
  body: string;
  confirmLabel: string;
  colour: string;
}

/**
 * What each action does to billing and to the client, in the words the admin
 * reads before confirming. Every one of these changes what a third party bills
 * the client for, so none of them happens on a single click.
 */
function confirmationFor(
  action: MonitoringEntitlementAction,
  entitlement: MonitoringEntitlement,
  teamName: string
): Confirmation {
  if (action === 'suspend') {
    return {
      action,
      title: `Suspend monitoring for ${teamName}?`,
      body:
        'Every subject stops being watched and is removed from Castellum, which stops ' +
        'billing. No alerts or digests are sent while suspended. Resuming registers them again.',
      confirmLabel: 'Suspend',
      colour: 'orange',
    };
  }
  if (action === 'end') {
    return {
      action,
      title: `End monitoring for ${teamName}?`,
      body:
        'Every subject is cancelled and removed from Castellum. Cancelled subjects cannot be ' +
        'resumed — monitoring them again means enrolling them again.',
      confirmLabel: 'End monitoring',
      colour: 'red',
    };
  }
  if (entitlement.state === 'ended') {
    return {
      action,
      title: `Reactivate monitoring for ${teamName}?`,
      body:
        'The team can be enrolled into monitoring again. Subjects cancelled when it ended stay ' +
        'cancelled and need enrolling again.',
      confirmLabel: 'Reactivate',
      colour: 'blue',
    };
  }
  return {
    action,
    title: `Resume monitoring for ${teamName}?`,
    body:
      'Suspended subjects are registered with Castellum again, which restarts billing, and each ' +
      'gets a fresh baseline screen.',
    confirmLabel: 'Resume',
    colour: 'blue',
  };
}

const DONE: Record<MonitoringEntitlementAction, string> = {
  suspend: 'Monitoring suspended',
  resume: 'Monitoring resumed',
  end: 'Monitoring ended',
};

/** Say plainly when billing or monitoring did not follow the state change. */
function reportChange(action: MonitoringEntitlementAction, change: MonitoringEntitlementChange) {
  if (change.complete) {
    notifications.show({ title: DONE[action], message: '', color: 'green' });
    return;
  }
  const problems: string[] = [];
  if (change.deregistrationFailures.length > 0) {
    problems.push(
      `Castellum is still billing for some subjects (${change.deregistrationFailures.join('; ')}). ` +
        'Removal is retried automatically every few minutes.'
    );
  }
  if (change.registrationError) {
    problems.push(
      `Subjects could not be registered and stay pending (${change.registrationError}). ` +
        'Registration is retried automatically.'
    );
  }
  if (change.baselineFailures.length > 0) {
    problems.push('Subjects are registered but have no baseline screen yet.');
  }
  notifications.show({
    title: `${DONE[action]}, but not everything went through`,
    message: problems.join(' '),
    color: 'orange',
    autoClose: false,
  });
}

/**
 * One team's monitoring entitlement: its state, and the actions that grant,
 * edit, suspend, resume or end it.
 */
export function MonitoringEntitlementControls({
  teamId,
  teamName,
  entitlement,
  onSaveDetails,
  onChange,
}: Readonly<MonitoringEntitlementControlsProps>) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [contractRef, setContractRef] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [busy, setBusy] = useState(false);

  const openDetails = () => {
    setContractRef(entitlement?.contractRef ?? '');
    setNotes(entitlement?.notes ?? '');
    setDetailsOpen(true);
  };

  const saveDetails = async () => {
    setBusy(true);
    try {
      await onSaveDetails(teamId, {
        contractRef: contractRef.trim() || null,
        notes: notes.trim() || null,
      });
      setDetailsOpen(false);
      notifications.show({
        title: entitlement ? 'Details saved' : 'Monitoring granted',
        message: '',
        color: 'green',
      });
    } catch (err) {
      Sentry.captureException(err instanceof Error ? err : new Error(String(err)));
      notifications.show({
        title: 'Could not save',
        message: err instanceof Error ? err.message : 'Please try again.',
        color: 'red',
      });
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!confirmation) {
      return;
    }
    setBusy(true);
    try {
      reportChange(confirmation.action, await onChange(teamId, confirmation.action));
      setConfirmation(null);
    } catch (err) {
      // A 409 means someone else changed it first; the row has been refetched.
      Sentry.captureException(err instanceof Error ? err : new Error(String(err)));
      notifications.show({
        title: 'Nothing was changed',
        message: err instanceof Error ? err.message : 'Please try again.',
        color: 'red',
      });
      setConfirmation(null);
    } finally {
      setBusy(false);
    }
  };

  const ask = (action: MonitoringEntitlementAction) => {
    if (entitlement) {
      setConfirmation(confirmationFor(action, entitlement, teamName));
    }
  };

  return (
    <>
      <Group gap="xs" wrap="nowrap" data-testid={`entitlement-${teamId}`}>
        {entitlement ? (
          <Badge color={STATE_COLOURS[entitlement.state]} variant="light">
            {MONITORING_ENTITLEMENT_STATE_LABELS[entitlement.state]}
          </Badge>
        ) : (
          <Badge color="gray" variant="outline">
            Not granted
          </Badge>
        )}
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={`Monitoring actions for ${teamName}`}
              disabled={busy}
            >
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {!entitlement && <Menu.Item onClick={openDetails}>Grant monitoring…</Menu.Item>}
            {entitlement && <Menu.Item onClick={openDetails}>Edit details…</Menu.Item>}
            {entitlement?.state === 'active' && (
              <Menu.Item onClick={() => ask('suspend')}>Suspend…</Menu.Item>
            )}
            {entitlement?.state === 'suspended' && (
              <Menu.Item onClick={() => ask('resume')}>Resume…</Menu.Item>
            )}
            {entitlement?.state === 'ended' && (
              <Menu.Item onClick={() => ask('resume')}>Reactivate…</Menu.Item>
            )}
            {(entitlement?.state === 'active' || entitlement?.state === 'suspended') && (
              <Menu.Item color="red" onClick={() => ask('end')}>
                End…
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>

      <Modal
        opened={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title={entitlement ? `Monitoring details — ${teamName}` : `Grant monitoring — ${teamName}`}
      >
        <Stack>
          {!entitlement && (
            <Text size="sm" c="dimmed">
              The team can then have report subjects enrolled into monitoring. Nothing is registered
              or billed until a subject is enrolled.
            </Text>
          )}
          <TextInput
            label="Contract reference"
            value={contractRef}
            onChange={(e) => setContractRef(e.currentTarget.value)}
          />
          <Textarea
            label="Notes"
            autosize
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDetailsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void saveDetails()} loading={busy}>
              {entitlement ? 'Save' : 'Grant monitoring'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={confirmation !== null}
        onClose={() => setConfirmation(null)}
        title={confirmation?.title}
      >
        <Stack>
          <Text size="sm">{confirmation?.body}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmation(null)}>
              Cancel
            </Button>
            <Button color={confirmation?.colour} onClick={() => void confirm()} loading={busy}>
              {confirmation?.confirmLabel}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
